import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Check } from 'lucide-react';
import { Card, CardContent, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();

  const handleAddToCart = () => {
    addToCart(product, 1);
    toast.success(`${product.name} added to cart!`);
  };

  // Calculate total stock from variants or use product stock
  const getTotalStock = () => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.reduce((total, variant) => total + (variant.stock || 0), 0);
    }
    // Use actual product stock from database
    return product.stock || 0;
  };

  const stockCount = getTotalStock();
  const isLowStock = stockCount > 0 && stockCount <= 10;
  const isOutOfStock = !product.in_stock || stockCount === 0;

  // Stock display helper
  const getStockDisplay = () => {
    if (isOutOfStock) {
      return { text: 'Out of Stock', color: 'bg-red-600', show: true };
    }
    if (isLowStock) {
      return { text: `Only ${stockCount} left!`, color: 'bg-yellow-600', show: true };
    }
    return { text: `${stockCount} in stock`, color: 'bg-green-600', show: true };
  };

  const stockDisplay = getStockDisplay();

  return (
    <Card className="group hover:shadow-lg transition-shadow duration-300">
      <Link to={`/product/${product.id}`}>
        <div className="relative overflow-hidden rounded-t-lg bg-gray-100">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {product.featured && (
            <Badge className="absolute top-2 right-2 bg-orange-600">Featured</Badge>
          )}
          {/* Stock Quantity Badge */}
          {stockDisplay.show && (
            <div className={`absolute top-2 left-2 ${stockDisplay.color} text-white px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 shadow-lg`}>
              <Check className="h-3 w-3" />
              <span>{stockDisplay.text}</span>
            </div>
          )}
        </div>
      </Link>
      
      <CardContent className="pt-4">
        <Link to={`/product/${product.id}`}>
          <p className="text-xs text-gray-500 mb-1">{product.category}</p>
          <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
            {product.name}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{product.description}</p>
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold text-gray-900">KES {product.price.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Per unit</p>
          </div>
        </div>
      </CardContent>
      
      <CardFooter>
        <Button
          onClick={handleAddToCart}
          className="w-full bg-orange-600 hover:bg-orange-700"
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          Add to Cart
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ProductCard;
